// Updated App.js with Series Support
import { useEffect, useMemo, useState } from "react";
import "./App.css";
import MarkdownPost from "./MarkdownPost";
import {
  findPost,
  getPostsBySeries,
  getSeries,
  getSeriesByCategory,
  getSeriesNavigation
} from "./posts";
import TableOfContents from "./TableOfContents";
import Navigation from "./Navigation";

// Service data
const services = [
  {
    id: "rf-comms",
    title: "RF Communications",
    icon: "/comms_hippo.png",
    description: "Cognitive radio systems, SDR implementations, signal processing, and FPGA-based wireless solutions.",
    features: ["Software-defined radio", "Signal processing", "Protocol development"]
  },
  {
    id: "ml-deployment",
    title: "ML/AI Deployment",
    icon: "/mlcloud_hippo.png",
    description: "Model optimization for edge devices, FPGA acceleration for inference, and real-time AI systems from cloud to hardware.",
    features: ["Edge optimization", "Hardware acceleration", "Real-time inference"]
  },
  {
    id: "hardware-accel",
    title: "ML Hardware Acceleration",
    icon: "/fpga_hippo.png",
    iconSize: "120px",
    description: "Putting ML on constrained devices for compute-intensive applications.",
    features: ["HLS4ML expertise", "Custom IP cores", "Performance tuning"]
  },
  {
    id: "finance",
    title: "Intelligent Claims",
    icon: "/finance_hippo.png",
    description: "Improving the claims journey through automating with ML.",
    features: ["Recovery", "Fraud", "Repair or Replace decisions", "Supplier Routing"]
  },
];

// --- Hash router with series support ---
function useHashRoute() {
  const parse = () => {
    const h = window.location.hash.replace(/^#\/?/, "") || "";
    if (!h) return { view: "home" };

    const parts = h.split("/").filter(Boolean);
    const section = parts[0];
    const slug = parts[1];

    if (section === "post" && slug) return { view: "post", slug };
    if (section === "series" && slug) return { view: "series", slug };
    if (section === "case-studies") return { view: "case-studies" };
    if (section === "guides") return { view: "guides" };
    return { view: "home" };
  };
  const [route, setRoute] = useState(parse);
  useEffect(() => {
    const onHash = () => setRoute(parse());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return route;
}

// --- Category Filter Component ---
function CategoryFilter({ activeCategory, onCategoryChange }) {
  return (
    <div className="category-filter">
      <button
        className={`filter-btn ${activeCategory === "business" ? "active" : ""}`}
        onClick={() => onCategoryChange("business")}
      >
        Business Case Studies
      </button>
      <button
        className={`filter-btn ${activeCategory === "guide" ? "active" : ""}`}
        onClick={() => onCategoryChange("guide")}
      >
        Tutorials/Guides
      </button>
    </div>
  );
}

// --- Series Card Component ---
function SeriesCard({ series, postsCount }) {
  return (
    <a href={`#/series/${series.slug}`} className="series-card-link">
      <div className="service-card series-card">
        <div className="service-icon">{series.icon}</div>
        <h3>{series.title}</h3>
        <p className="service-description">{series.description}</p>
        <div className="series-meta">
          <span className="post-category-badge">{postsCount} Part{postsCount !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </a>
  );
}

// --- Service Card Component ---
function ServiceCard({ service }) {
  return (
    <div className="service-card">
      {service.icon.startsWith('/') ? (
        <img
          src={service.icon}
          alt={service.title}
          className="service-icon-img"
          style={service.iconSize ? { width: service.iconSize, height: service.iconSize } : {}}
        />
      ) : (
        <div className="service-icon">{service.icon}</div>
      )}
      <h3>{service.title}</h3>
      <p className="service-description">{service.description}</p>
      <ul className="service-features">
        {service.features.map((feature, i) => (
          <li key={i}>{feature}</li>
        ))}
      </ul>
    </div>
  );
}

// --- Series Navigation Component ---
function SeriesNavigation({ navigation }) {
  if (!navigation.prev && !navigation.next) return null;

  return (
    <div className="series-navigation">
      <div className="series-nav-header">
        <strong>{navigation.seriesInfo?.title}</strong>
      </div>
      <div className="series-nav-links">
        {navigation.prev ? (
          <a href={`#/post/${navigation.prev.slug}`} className="series-nav-link prev">
            ← Previous: {navigation.prev.title}
          </a>
        ) : <div />}
        {navigation.next ? (
          <a href={`#/post/${navigation.next.slug}`} className="series-nav-link next">
            Next: {navigation.next.title} →
          </a>
        ) : <div />}
      </div>
    </div>
  );
}

// --- Pages ---
function HomePage() {
  useEffect(() => {
    document.title = "Stomping Hippo - From the cloud to FPGAs";
    setMetaDescription("Expert consulting in finance systems, RF communications, ML deployment, and hardware acceleration.");
  }, []);

  return (
    <>
      <Navigation currentView="home" />
      <main className="container">
        <section className="services-intro">
          <h2>Areas of interest</h2>
          <p>Specialized expertise in high-performance systems, from cloud infrastructure to machine learning on FPGAs.</p>
        </section>

        <div className="services-grid">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        <section className="cta-section">
          <h3>Have a mutual interest?</h3>
          <a href="mailto:contact@stompinghippo.com" className="cta-button">Get in Touch</a>
        </section>

        <SiteFooter showCaseStudies />
      </main>
    </>
  );
}

//<h2>Areas of interest</h2>
//<h3>Ready to discuss your project?</h3>
//<p>Let's explore how we can help accelerate your systems.</p>

function CaseStudiesPage() {
  const [activeCategory, setActiveCategory] = useState("guide");

  const filteredSeries = useMemo(() => {
    return getSeriesByCategory(activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    document.title = "Case Studies & Insights - Stomping Hippo";
    setMetaDescription("Technical articles, project showcases, and tutorials on ML systems, RF, and hardware acceleration.");
  }, []);

  return (
    <>
      <Navigation currentView="case-studies" />
      <main className="container">
        <div className="case-studies-header">
          <h1>Case Studies & Technical Insights</h1>
          <p>In-depth article series on real-world implementations and technical deep-dives.</p>
        </div>

        <CategoryFilter
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />

        <div className="services-grid">
          {filteredSeries.map((s) => (
            <SeriesCard
              key={s.slug}
              series={s}
              postsCount={getPostsBySeries(s.slug).length}
            />
          ))}
        </div>

        {filteredSeries.length === 0 && (
          <div className="empty-state">
            <p>No {activeCategory === "business" ? "business case studies" : "tutorial series"} available yet.</p>
          </div>
        )}

        <SiteFooter />
      </main>
    </>
  );
}

function SeriesPage({ slug }) {
  const seriesInfo = getSeries(slug);
  const seriesPosts = useMemo(() => getPostsBySeries(slug), [slug]);

  useEffect(() => {
    const t = seriesInfo ? `${seriesInfo.title} - Stomping Hippo` : "Series not found";
    document.title = t;
    setMetaDescription(seriesInfo ? seriesInfo.description : "Series not found.");
  }, [seriesInfo]);

  if (!seriesInfo) {
    return (
      <>
        <Navigation currentView="case-studies" />
        <main className="container">
          <div className="back-link-wrapper">
            <a href="#/case-studies" className="back-link">← Case Studies</a>
          </div>
          <p>Series not found. <a href="#/case-studies">Go back to case studies</a>.</p>
          <SiteFooter />
        </main>
      </>
    );
  }

  return (
    <>
      <Navigation currentView="case-studies" />
      <main className="container">
        <div className="back-link-wrapper">
          <a href="#/case-studies" className="back-link">← Case Studies</a>
        </div>

        <div className="case-studies-header">
          <div className="series-icon-large">{seriesInfo.icon}</div>
          <h1>{seriesInfo.title}</h1>
          <p>{seriesInfo.description}</p>
        </div>

        <ul className="post-list">
          {seriesPosts.map((p) => (
            <li key={p.slug} className="post-item">
              <h2>
                <a href={`#/post/${p.slug}`}>{p.title}</a>
              </h2>
              <time dateTime={p.date}>{prettyDate(p.date)}</time>
              <p className="excerpt">{p.excerpt}</p>
            </li>
          ))}
        </ul>

        {seriesPosts.length === 0 && (
          <div className="empty-state">
            <p>No posts in this series yet.</p>
          </div>
        )}

        <SiteFooter />
      </main>
    </>
  );
}

function PostView({ slug }) {
  const post = findPost(slug);
  const [headings, setHeadings] = useState([]);
  const navigation = post ? getSeriesNavigation(post) : null;

  useEffect(() => {
    const t = post ? `${post.title} - Stomping Hippo` : "Not found - Stomping Hippo";
    document.title = t;
    setMetaDescription(post ? post.excerpt : "Post not found.");
  }, [post]);

  if (!post) return <NotFound />;

  return (
    <>
      <Navigation currentView="post" />
      <main className="container">
        <div className="back-link-wrapper">
          {post.series ? (
            <a href={`#/series/${post.series}`} className="back-link">
              ← Back to {navigation?.seriesInfo?.title}
            </a>
          ) : (
            <a href="#/case-studies" className="back-link">← Case Studies</a>
          )}
        </div>

        {navigation && navigation.seriesInfo && (
          <SeriesNavigation navigation={navigation} />
        )}

        <div className="post-layout">
          {headings.length > 0 && <TableOfContents headings={headings} />}
          <article className="post">
            <h1>{post.title}</h1>
            <time dateTime={post.date}>{prettyDate(post.date)}</time>
            <div className="post-body">
              {post.mdPath ? (
                <MarkdownPost src={post.mdPath} onHeadingsExtracted={setHeadings} />
              ) : post.html ? (
                <div dangerouslySetInnerHTML={{ __html: post.html }} />
              ) : (
                <p>No content found for this post.</p>
              )}
            </div>
          </article>
        </div>

        {navigation && navigation.seriesInfo && (
          <SeriesNavigation navigation={navigation} />
        )}

        <SiteFooter />
      </main>
    </>
  );
}

function NotFound() {
  useEffect(() => {
    document.title = "Not found - Stomping Hippo";
    setMetaDescription("The page you're looking for does not exist.");
  }, []);
  return (
    <>
      <Navigation currentView="" />
      <main className="container">
        <p>
          Page not found. <a href="#/">Go home</a>.
        </p>
        <SiteFooter />
      </main>
    </>
  );
}

// --- Layout ---
function SiteFooter({ showCaseStudies }) {
  return (
    <footer className="site-footer">
      <div className="footer-brand">
        <p className="footer-tagline">From the cloud to FPGAs</p>
      </div>
      <p className="footer-copyright">&copy; {new Date().getFullYear()} Stomping Hippo</p>
    </footer>
  );
}

// --- Utilities ---
function prettyDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function setMetaDescription(content) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function Hero() {
  return (
    <div className="hero-bleed">
      <div className="hero-inner">
        <div className="hero-content">
          <div className="hero-kicker">AI &amp; RF</div>
          <h1>From the cloud to FPGAs</h1>
          <p>Expert in ML systems, edge deployments, and hardware acceleration.</p>
        </div>
        <div className="hero-logo">
          <img src="/stompinghippo_logo.png" alt="Stomping Hippo" />
        </div>
      </div>
    </div>
  );
}

// --- App root ---
export default function App() {
  const route = useHashRoute();

  let page;
  if (route.view === "post") {
    page = <PostView slug={route.slug} />;
  } else if (route.view === "series") {
    page = <SeriesPage slug={route.slug} />;
  } else if (route.view === "case-studies") {
    page = <CaseStudiesPage />;
  } else if (route.view === "guides") {
    page = <HomePage />; // You can create a GuidesPage if needed
  } else {
    page = <HomePage />;
  }

  return (
    <>
      {route.view === "home" && <Hero />}
      {page}
    </>
  );
}
